import * as React from 'react';
import { useIntl } from 'react-intl';

import { Text } from '@notino/react-styleguide';

import { useAnimateUnmount } from '@components/Animate/useAnimateUnmount';

import { SHADEFINDER_CONFIG } from '../../config';
import { useSaveShadefinderUserData } from '../../hooks/useSaveShadefinderUserData';
import { useShadefinderData } from '../../hooks/useShadefinderData';
import { useShadefinderTransitions } from '../../hooks/useShadefinderTransitions';
import { useTrackSelect } from '../../hooks/useTrackSelect';
import { messages } from '../../messages';
import { trackShadefinderSelectShade } from '../../tracking';
import { ShadefinderShadesKeys, ShadefinderShadeUndertone } from '../../types';
import { Item } from '../Item';
import { StepContainer } from '../StepContainer';

import { ShadefinderStep2 } from './Step2';

export interface IShadefinderStep1Props {
  masterId: string;
  timeOfStart: number;
}

const STEP_2_PROPS = {
  currentStep: 2,
  name: 'select_undertone',
};

export const ShadefinderStep1: React.FC<IShadefinderStep1Props> = ({
  masterId,
  timeOfStart,
}) => {
  const { formatMessage } = useIntl();
  const { transition, animateProgress } = useShadefinderTransitions();
  const { data, loading } = useShadefinderData(masterId);
  const { onShadefinderUserDataChanged } = useSaveShadefinderUserData(masterId);
  const [unmounting, animateTransition] = useAnimateUnmount();

  const trackSelect = useTrackSelect(timeOfStart);

  const isLoaded = !loading && data?.getShadefinder?.shades;
  const renderableOrderedShades = Object.keys(SHADEFINDER_CONFIG.shades).filter(
    (key) => key in (data?.getShadefinder?.shades || {})
  );

  return (
    <StepContainer
      unmounting={unmounting}
      header={formatMessage(messages.chooseSkinShadeTitle)}
      subheader={formatMessage(messages.chooseSkinShadeDescription)}
    >
      {isLoaded &&
        renderableOrderedShades.map((key: ShadefinderShadesKeys) => {
          const undertones = data.getShadefinder!.shades[
            key
          ] as ShadefinderShadeUndertone;

          const label = formatMessage(SHADEFINDER_CONFIG.shades[key].label);
          const imageUrl = SHADEFINDER_CONFIG.shades[key].imageUrl;

          const id = `shade-${key}`;
          return (
            <Item
              imageUrl={imageUrl}
              key={key}
              labelledby={id}
              onClick={animateTransition(
                () =>
                  transition(
                    <ShadefinderStep2
                      masterId={masterId}
                      shade={key}
                      undertones={undertones}
                      timeOfStart={timeOfStart}
                    />,
                    STEP_2_PROPS
                  ),
                {
                  beforeTransition: () => {
                    animateProgress(STEP_2_PROPS);
                    trackSelect((props) =>
                      trackShadefinderSelectShade({ ...props, name: key })
                    );
                    void onShadefinderUserDataChanged({ skinTone: key });
                  },
                }
              )}
            >
              <Text typography="labelLarge400" color="text.primary" id={id}>
                {label}
              </Text>
            </Item>
          );
        })}
    </StepContainer>
  );
};
